import {ControlledSwitch, H4, Icon, Loading} from '@hm/fundamental'
import {buildFeatureUrl, usePlantHierarchyQuery} from '@hproduce/suite-core'
import {mdiMapMarkerOutline} from '@mdi/js'
import {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router'

import {MenuNodeType} from '../../../api/types/featuresMenu'
import {useFeaturesMenu} from '../../../app/welcomeFlow/useFeaturesMenu'
import {FeatureMenuNode} from '../sideMenu/features-tree.utils'
import {FeaturesTree} from '../sideMenu/FeaturesTree'
import {SettingsAppLink} from '../sideMenu/SettingsAppLink'

import {getCurrentLocation} from './getCurrentLocation'
import {useHierarchyIdFromURL} from './PlantSelectButton/useHierarchyIdFromUrl'
import {PlantSelectTree} from './PlantSelectTree'

enum MobileMenuSwitchOptions {
  Menu = 'Menu',
  Location = 'Location'
}

interface MobileMenuContentProps {
  onMenuClose?: () => void
}

export const MobileMenuContent = ({onMenuClose}: MobileMenuContentProps) => {
  const {t} = useTranslation()

  const navigate = useNavigate()

  const switchOptions = [
    {label: t(MobileMenuSwitchOptions.Menu), value: MobileMenuSwitchOptions.Menu},
    {label: t(MobileMenuSwitchOptions.Location), value: MobileMenuSwitchOptions.Location}
  ]

  const [selectedOption, setSelectedOption] = useState<string | number>(switchOptions[0].value)
  const {featuresMenu, isLoading} = useFeaturesMenu()

  const {data: plantHierarchyData, isPending} = usePlantHierarchyQuery()
  const [hierarchyId, setHierarchyId] = useHierarchyIdFromURL()

  const onSelectHierarchyLevel = useCallback(
    (hierarchyId: string) => {
      setHierarchyId(hierarchyId)
      onMenuClose?.()
    },
    [setHierarchyId, onMenuClose]
  )

  const currentLocation = useMemo(() => {
    if (!plantHierarchyData || !hierarchyId) return

    return getCurrentLocation(hierarchyId, plantHierarchyData)
  }, [hierarchyId, plantHierarchyData])

  const handleSelect = useCallback(
    (node?: FeatureMenuNode) => {
      const isFeatureNode = node && node.type === MenuNodeType.Feature
      if (isFeatureNode) {
        void navigate(buildFeatureUrl(node.name, hierarchyId))
        onMenuClose?.()
      }
    },
    [navigate, hierarchyId, onMenuClose]
  )

  const featureMenuTree = (
    <div className="flex flex-col gap-4">
      <SettingsAppLink />
      {featuresMenu ? (
        <FeaturesTree featuresMenu={featuresMenu} onSelectItem={handleSelect} />
      ) : null}
    </div>
  )

  const localization = (
    <PlantSelectTree
      isLoading={isPending}
      nodes={plantHierarchyData}
      onSelect={onSelectHierarchyLevel}
      isOnDarkBackground={true}
    />
  )

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loading size="small" data-cy="feature_menu_loader" />
      </div>
    )
  }

  const componentMap: Record<MobileMenuSwitchOptions, React.ReactNode> = {
    [MobileMenuSwitchOptions.Location]: localization,
    [MobileMenuSwitchOptions.Menu]: featureMenuTree
  }

  return (
    <div
      className="flex flex-col gap-4"
      data-test-id="mobile-menu-content"
      data-cy="mobile_menu_content"
    >
      <div
        className="flex items-center justify-center"
        onClick={() => setSelectedOption(MobileMenuSwitchOptions.Location)}
      >
        {currentLocation && (
          <div className="flex items-center justify-center">
            <Icon path={mdiMapMarkerOutline} color="white" />
            <H4 style={{color: 'white'}}>{currentLocation}</H4>
          </div>
        )}
      </div>
      <div className="flex justify-center w-full">
        <ControlledSwitch
          options={switchOptions}
          onChange={setSelectedOption}
          value={selectedOption}
          data-cy="mobile_menu_switch"
        />
      </div>
      {componentMap[selectedOption]}
    </div>
  )
}
